import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowPathIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useStudioUser } from '../../hooks/useStudioUser';
import { useStudioPlan } from '../../hooks/useStudioPlan';
import Notification from '../Notification';
import UpgradePrompt from '../UpgradePrompt';
import DOMPurify from 'dompurify';

// Security constants
const MAX_LENGTHS: Record<string, number> = {
  conflictId: 50,
  resolution: 20,
  mode: 20,
};

const SUBMISSION_COOLDOWN = 5000; // 5 seconds between sync operations (longer due to critical nature)

// Input sanitization function
const sanitizeInput = (input: string, fieldType?: string): string => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // Remove dangerous patterns
  sanitized = sanitized.replace(/[<>]/g, ''); // Remove HTML tags
  sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript protocol
  sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript protocol
  sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove event handlers
  sanitized = sanitized.replace(/data:text\/html/gi, ''); // Remove data URLs
  
  return sanitized.trim();
};

// Enhanced validation function
const validateInput = (value: string, type: string): boolean => {
  if (!value || typeof value !== 'string') return false;
  
  const sanitized = sanitizeInput(value, type);
  if (sanitized.length === 0) return false;

  // Check length limits
  if (MAX_LENGTHS[type] && sanitized.length > MAX_LENGTHS[type]) {
    return false;
  }
  
  // Type-specific validation
  switch (type) {
    case 'mode':
      // Validate sync mode values
      const validModes = ['import', 'export', 'bidirectional'];
      return validModes.includes(sanitized);
    case 'resolution':
      // Validate conflict resolution values
      const validResolutions = ['keep_local', 'keep_stripe', 'merge', 'skip'];
      return validResolutions.includes(sanitized);
    case 'conflictId':
      // Validate conflict ID format (alphanumeric and some special chars)
      const conflictIdDangerousPatterns = /[<>]|javascript:|vbscript:|on\w+\s*=/i;
      return !conflictIdDangerousPatterns.test(sanitized) && sanitized.length >= 1;
    default:
      return sanitized.length > 0;
  }
};

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  console.warn(`Security Event: ${event}`, {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    details
  });
};

interface SyncStatus {
  localStudents: number;
  stripeCustomers: number;
  studentsWithStripeId: number;
  studentsWithoutStripeId: number;
  stripeCustomersWithoutStudents: number;
  lastSyncTime: string | null;
  syncInProgress: boolean;
}



const SyncSettings: React.FC = () => {
  const { getAuthToken } = useAuth();
  const { studioUser } = useStudioUser();
  const { hasCapability, loading: planLoading, isFreePlan } = useStudioPlan();
  
  // Security-related state
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);
  
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [syncMode, setSyncMode] = useState<'import' | 'export' | 'bidirectional'>('bidirectional');
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    title: '',
    type: 'success' as 'success' | 'error' | 'general',
  });

  // Secure sync mode change handler
  const handleSyncModeChange = (newMode: string) => {
    if (!validateInput(newMode, 'mode')) {
      logSecurityEvent('INVALID_SYNC_MODE_ATTEMPT', { mode: newMode });
      setNotification({
        show: true,
        message: "Invalid sync mode selected",
        title: "Validation Error",
        type: "error",
      });
      return;
    }
    setSyncMode(newMode as 'import' | 'export' | 'bidirectional');
  };



  // Fetch CSRF token
  const fetchCsrfToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/csrf-token`);
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          setCsrfToken(data.token);
        } else {
          logSecurityEvent('CSRF_TOKEN_INVALID_RESPONSE', { data });
        }
      } else {
        logSecurityEvent('CSRF_TOKEN_FETCH_ERROR', { status: response.status, statusText: response.statusText });
      }
    } catch (error) {
      logSecurityEvent('CSRF_TOKEN_FETCH_FAILED', { error: error instanceof Error ? error.message : 'Unknown error' });
      // In development, generate a fallback token
      if (process.env.NODE_ENV !== 'production') {
        const fallbackToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
        setCsrfToken(fallbackToken);
        console.warn('Using fallback CSRF token in development mode');
      }
    }
  };

  // Fetch sync status
  const fetchSyncStatus = async () => {
    try {
      const authToken = getAuthToken();
      if (!authToken) {
        throw new Error("Authentication required");
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stripe/sync-status`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "X-CSRF-Token": csrfToken,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch sync status: ${response.status}`);
      }
      
      const data = await response.json();
      setSyncStatus(data);
    } catch (error) {
      logSecurityEvent('SYNC_STATUS_FETCH_FAILED', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        studioId: studioUser?.studio_id
      });
      setNotification({
        show: true,
        message: 'Failed to fetch sync status',
        title: 'Error',
        type: 'error',
      });
    }
  };



  // Perform sync
  const performSync = async () => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before performing another sync operation",
        title: "Error",
        type: "error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'performSync' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
          type: "error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with sync');
      }
    }

    // Validate sync mode
    if (!validateInput(syncMode, 'mode')) {
      logSecurityEvent('INVALID_SYNC_MODE', { mode: syncMode });
      setNotification({
        show: true,
        message: "Invalid sync mode selected",
        title: "Validation Error",
        type: "error",
      });
      return;
    }



    setIsLoading(true);
    setLastSubmissionTime(now);
    
    try {
      const authToken = getAuthToken();
      if (!authToken) {
        throw new Error("Authentication required");
      }

      // Prepare and sanitize request body
      const requestBody = {
        mode: sanitizeInput(syncMode, 'mode'),
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stripe/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Sync failed: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        await fetchSyncStatus();
        logSecurityEvent('SYNC_COMPLETED_SUCCESSFULLY', {
          mode: syncMode,
          imported: result.results?.imported || 0,
          exported: result.results?.exported || 0,
          studioId: studioUser?.studio_id
        });
        setNotification({
          show: true,
          message: `Sync completed successfully! Imported: ${result.results.imported}, Exported: ${result.results.exported}${result.results.orphanedRecordsCleaned > 0 ? `, Cleaned ${result.results.orphanedRecordsCleaned} orphaned records` : ''}`,
          title: 'Sync Complete',
          type: 'success',
        });
      } else {
        logSecurityEvent('SYNC_FAILED', { 
          error: result.error,
          mode: syncMode,
          studioId: studioUser?.studio_id
        });
        setNotification({
          show: true,
          message: result.error || 'Sync failed',
          title: 'Sync Error',
          type: 'error',
        });
      }
    } catch (error) {
      logSecurityEvent('SYNC_OPERATION_FAILED', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        mode: syncMode,
        studioId: studioUser?.studio_id
      });
      setNotification({
        show: true,
        message: 'Sync failed. Please try again.',
        title: 'Sync Error',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (studioUser?.studio_id) {
      fetchSyncStatus();
    }
  }, [studioUser?.studio_id]);

  // Fetch CSRF token on component mount
  useEffect(() => {
    fetchCsrfToken();
  }, []); // Empty dependency array - only run once

  if (planLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
        </div>
      </div>
    );
  }

  if (isFreePlan || !hasCapability('stripeConnect')) {
    return (
      <div className="space-y-6">
        <UpgradePrompt capability="stripeConnect" />
      </div>
    );
  }

  if (!syncStatus) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  // Show message if no Stripe connection
  if (!syncStatus.stripeCustomers && syncStatus.localStudents > 0) {
    return (
      <div className="space-y-6">
        <Notification
          show={notification.show}
          message={notification.message}
          title={notification.title}
          type={notification.type}
          onClose={() => setNotification(prev => ({ ...prev, show: false }))}
        />

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Sync Status
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{syncStatus.localStudents}</div>
              <div className="text-sm text-gray-500">Local Students</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-400">0</div>
              <div className="text-sm text-gray-500">Stripe Customers</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{syncStatus.studentsWithoutStripeId}</div>
              <div className="text-sm text-gray-500">Need Stripe ID</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-400">0</div>
              <div className="text-sm text-gray-500">Need Local Record</div>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <h3 className="text-lg font-medium text-blue-800 dark:text-blue-200 mb-4">
            Connect Stripe to Enable Sync
          </h3>
          <p className="text-blue-700 dark:text-blue-300 mb-4">
            To sync your students with Stripe for billing and payment management, you need to connect your Stripe account first.
          </p>
          <Link
            to="/app/settings/integrations"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <span className="pointer-events-none">Connect Stripe Account</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Notification
        show={notification.show}
        message={notification.message}
        title={notification.title}
        type={notification.type}
        onClose={() => setNotification(prev => ({ ...prev, show: false }))}
      />

      {/* Sync Status Overview */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Sync Status
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{syncStatus.localStudents}</div>
            <div className="text-sm text-gray-500">Local Students</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{syncStatus.stripeCustomers}</div>
            <div className="text-sm text-gray-500">Stripe Customers</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">{syncStatus.studentsWithoutStripeId}</div>
            <div className="text-sm text-gray-500">Need Stripe ID</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{syncStatus.stripeCustomersWithoutStudents}</div>
            <div className="text-sm text-gray-500">Need Local Record</div>
          </div>
        </div>

        {syncStatus.lastSyncTime && (
          <div className="mt-4 text-sm text-gray-500">
            Last synced: {new Date(syncStatus.lastSyncTime).toLocaleString()}
          </div>
        )}
        

      </div>

      {/* Sync Mode Selection */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Sync Options
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="bidirectional"
                checked={syncMode === 'bidirectional'}
                onChange={(e) => handleSyncModeChange(e.target.value)}
                className="mr-2"
              />
              <span className="text-sm">Bidirectional Sync (Recommended)</span>
            </label>
          </div>
          
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="import"
                checked={syncMode === 'import'}
                onChange={(e) => handleSyncModeChange(e.target.value)}
                className="mr-2"
              />
              <span className="text-sm">Import from Stripe Only</span>
            </label>
          </div>
          
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="export"
                checked={syncMode === 'export'}
                onChange={(e) => handleSyncModeChange(e.target.value)}
                className="mr-2"
              />
              <span className="text-sm">Export to Stripe Only</span>
            </label>
          </div>
        </div>
      </div>



      {/* Sync Button */}
      <div className="flex justify-center">
        <button
          onClick={performSync}
          disabled={isLoading || syncStatus.syncInProgress}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <ArrowPathIcon className="animate-spin -ml-1 mr-2 h-4 w-4 pointer-events-none" />
              <span className="pointer-events-none">Syncing...</span>
            </>
          ) : (
            <>
              <ArrowPathIcon className="-ml-1 mr-2 h-4 w-4 pointer-events-none" />
              <span className="pointer-events-none">Sync Now</span>
            </>
          )}
        </button>
      </div>

      {/* Help Text */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
          About Sync
        </h4>
        <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
          <li>• <strong>Bidirectional Sync:</strong> Imports new Stripe customers and exports local students to Stripe</li>
          <li>• <strong>Import Only:</strong> Only brings new customers from Stripe into your app</li>
          <li>• <strong>Export Only:</strong> Only creates Stripe customers for your local students</li>
          <li>• Conflicts are automatically detected and can be resolved before syncing</li>
        </ul>
      </div>
    </div>
  );
};

export default SyncSettings; 